import { useWatch, type Control } from 'react-hook-form'

import { MoneyField, TextField } from '@/components/form/fields'

import type { DuplicateCheck } from '../use-duplicate-check'
import { isGhanaPhoneShape, normalisePhoneInput } from '../validation'
import type { CustomerWizardInput } from '../validation'
import { DuplicateNotice } from './duplicate-notice'
import { FormattedTextField } from './formatted-field'
import { StepSection } from './step-section'

export interface ContactStepProps {
  control: Control<CustomerWizardInput>
  duplicate: DuplicateCheck
}

/** Spaces and dashes are stripped as they are typed; the trunk prefix is left exactly as keyed. */
const tidyPhone = (raw: string) => raw.replace(/[\s()\-.]/g, '')

export function ContactStep({ control, duplicate }: ContactStepProps) {
  const phoneNumber = useWatch({ control, name: 'phoneNumber' })

  return (
    <div className="space-y-6">
      <StepSection
        title="Reaching the customer"
        description="The primary number is the SMS target for transaction alerts, and the second key the institution deduplicates on."
      >
        <FormattedTextField
          control={control}
          name="phoneNumber"
          label="Mobile number"
          required
          inputMode="tel"
          placeholder="0241234567"
          description="Local or +233 form; both are stored the same way."
          format={tidyPhone}
          onValueBlur={(value) => {
            if (isGhanaPhoneShape(value)) {
              // Normalised first, because 0241234567 and +233241234567 are the same person and the
              // server judges uniqueness on the normalised form.
              duplicate.check(normalisePhoneInput(value))
            }
          }}
          className="sm:col-span-2"
        >
          <DuplicateNotice
            field="phoneNumber"
            status={duplicate.statusFor(normalisePhoneInput(phoneNumber))}
            existingCustomerId={duplicate.existingCustomerId}
          />
        </FormattedTextField>

        <FormattedTextField
          control={control}
          name="altPhoneNumber"
          label="Alternate mobile"
          inputMode="tel"
          description="Optional."
          format={tidyPhone}
        />
        <TextField
          control={control}
          name="email"
          label="Email"
          type="email"
          inputMode="email"
          autoComplete="off"
          description="Optional."
        />
      </StepSection>

      <StepSection
        title="Livelihood"
        description="What the customer does, and roughly what it brings in. This is the baseline that later account activity is judged against, so an honest estimate is worth more than a round number."
      >
        <TextField control={control} name="occupation" label="Occupation" required />
        <TextField
          control={control}
          name="employerOrBusinessName"
          label="Employer or business"
          description="Where they work, or the name and place of the business."
        />
        <MoneyField
          control={control}
          name="monthlyIncomeEstimateMinor"
          label="Estimated monthly income"
          required
          description="As estimated with the customer. It is an estimate, not a declaration."
        />
      </StepSection>
    </div>
  )
}
